import type { ReactNode } from "react";
import { createContext, createElement, useCallback, useContext, useLayoutEffect, useMemo, useState } from "react";

export type Theme = "light" | "dark";

const THEME_STORAGE_KEY = "theme";

const resolveTheme = (): Theme => {
	if (typeof window === "undefined") {
		return "light";
	}

	try {
		const stored = localStorage.getItem(THEME_STORAGE_KEY);
		if (stored === "light" || stored === "dark") {
			return stored;
		}
	} catch {
		// Ignore storage access failures and fall back to media query/default.
	}

	if (window.matchMedia?.("(prefers-color-scheme: dark)").matches) {
		return "dark";
	}

	return "light";
};

const applyThemeToRoot = (theme: Theme) => {
	if (typeof document === "undefined") return;

	const root = document.documentElement;
	if (theme === "dark") {
		root.classList.add("dark");
		return;
	}
	root.classList.remove("dark");
};

const persistTheme = (theme: Theme) => {
	if (typeof window === "undefined") return;

	try {
		localStorage.setItem(THEME_STORAGE_KEY, theme);
	} catch {
		// Ignore storage access failures.
	}
};

interface ThemeContextValue {
	theme: Theme;
	setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

interface ThemeProviderProps {
	children: ReactNode;
}

export const ThemeProvider = ({ children }: ThemeProviderProps) => {
	const [theme, setThemeState] = useState<Theme>(() => {
		const initialTheme = resolveTheme();
		applyThemeToRoot(initialTheme);
		return initialTheme;
	});

	const syncThemeState = useCallback((nextTheme: Theme) => {
		applyThemeToRoot(nextTheme);
		setThemeState((currentTheme) => (currentTheme === nextTheme ? currentTheme : nextTheme));
	}, []);

	useLayoutEffect(() => {
		persistTheme(theme);
	}, [theme]);

	useLayoutEffect(() => {
		const handleStorage = (event: StorageEvent) => {
			if (event.key !== THEME_STORAGE_KEY) return;
			syncThemeState(resolveTheme());
		};

		window.addEventListener("storage", handleStorage);
		return () => {
			window.removeEventListener("storage", handleStorage);
		};
	}, [syncThemeState]);

	const setTheme = useCallback((nextTheme: Theme) => syncThemeState(nextTheme), [syncThemeState]);

	const contextValue = useMemo<ThemeContextValue>(
		() => ({
			theme,
			setTheme,
		}),
		[theme, setTheme],
	);

	return createElement(ThemeContext.Provider, { value: contextValue }, children);
};

export const useTheme = (): [Theme, (theme: Theme) => void] => {
	const context = useContext(ThemeContext);
	if (!context) {
		throw new Error("useTheme must be used within ThemeProvider");
	}

	return [context.theme, context.setTheme];
};
